// Keeps personal and health details out of web BugSnag reports.
//
// Kickoff has no BAA with BugSnag, so a report may carry identifiers but never
// content: see `documentation/explainers/observability/README.md`. Callers
// attach whole client, coach and call records with `addMetadata`, so
// `redactReportData` walks metadata at every depth and replaces the values of
// personal keys before the report leaves. It is a key denylist, so it cannot
// know every key: call sites still send ids, and this is the safety net. The
// key rules are a copy of `node/utilities/error/redact-report.ts` (#6962),
// whose unit test checks them against the sanitizer manifest; keep the two in
// step (the one difference is SCRUBBED_TEXT_KEYS below).
// REDACTED_KEYS in clients/bugsnag.ts still replaces credential keys.

export const FILTERED = '[Filtered]'
export const TRUNCATED = '[Truncated]'
export const CIRCULAR = '[Circular]'
// A value whose walk threw (a throwing getter or toJSON): replaced, never sent
// as it was.
export const UNREDACTABLE = '[Unredactable]'

// Deep enough for a client with its user, coach and the coach's user, shallow
// enough that a runaway graph is cut off rather than serialized.
export const MAX_DEPTH = 10

// Objects and arrays walked per call, so a huge graph is cut off, not walked.
export const MAX_NODES = 5000

// Identifiers that are themselves personal data: a member or policy id names a
// person to their payer. They lose the identifier exemption below. Broad on
// purpose, so they also catch keys such as `teamMembers` and `retryPolicy`.
const SENSITIVE_ID_PATTERNS: RegExp[] = [
  /member/i,
  /policy/i,
  /subscriber/i,
  /groupNumber/i,
  // Not a bare /ssn/: that matches `areClientProblemsSnoozed`.
  /^ssn$|Ssn$|socialSecurity/i,
  /medicalRecord/i,
]

// A health fact, including a clinical note's sections and a wearable's
// readings. A flag directly under one of these keys is the fact itself:
// `hasDiagnosis: true` next to a client id says this person has a diagnosis.
const HEALTH_PATTERNS: RegExp[] = [
  /diagnos|injur|medication|medical|icd10|symptom|allerg/i,
  /chiefComplaint|assessment|intervention|evaluation|patientInformation/i,
  /blood|glucose|oxygen|heartRate|heartData|respiration|menstrua|hydration/i,
]

// `id`, `clientId`, `ids`: a pointer to a record, which is the point.
const IDENTIFIER_PATTERNS: RegExp[] = [/^ids?$/i, /Ids?$/]

// Names of code and infrastructure, not of people. `/name$/` has to keep
// denying `firstName`, and no pattern separates the two.
const SAFE_NAME_KEYS = new Set([
  'eventName',
  'jobName',
  'operationName',
  'handlerName',
  'queueName',
  'tableName',
  'templateName',
  'fieldName',
  'ruleName',
  'errorName',
  'className',
  'functionName',
  'serviceName',
  'bucketName',
  'columnName',
  'constraintName',
  'indexName',
  'modelName',
  'relationName',
  'hostname',
  'pathname',
  // GraphQL's `__typename`, once its leading underscores are dropped.
  'typename',
])

// Error text, which the string scrub cleans like a report's own message:
// BugSnag's error breadcrumbs carry the earlier error's `errorMessage`. Web
// only: the node copy filters it, since node has no breadcrumbs and a stored
// vendor error (candid_submission_logs.error_message) can echo a patient.
const SCRUBBED_TEXT_KEYS = new Set(['errorMessage'])

// Keys that hold a person's identity, health, insurance or words. Everything
// under one is personal too, apart from identifiers and flags:
// `address: { line1, zip }` loses both values, `diagnoses: [{ id, code }]`
// keeps each id.
const PERSONAL_PATTERNS: RegExp[] = [
  /e-?mail/i,
  /phone/i,
  /names?$/i,
  /address/i,
  /birth|^dob$/i,
  /^(latitude|longitude)$/i,
  /(from|to|destination)Number$/i,
  /interviewer|participantIdentity/i,
  /insurance|transcript/i,
  ...HEALTH_PATTERNS,
]

// Details about a person, and free text a person wrote or said or an AI wrote
// about them. Only the scalar values under these keys are replaced. An object
// under one is walked like any other: `callNotes` is a tab of ids, and
// `clientEligibilityStatuses` rows keep their ids, states and timestamps while
// their policy and payer fields go.
const DETAIL_PATTERNS: RegExp[] = [
  /^(street|city|zip|zipCode|postalCode|postcode)$/i,
  /^(gender|sex|bmi)$/i,
  /(weight|height)$/i,
  /^ip$|Ip$/,
  /^slug$|^inviteCode$/i,
  /eligibility|copay|coinsurance|deductible|outOfPocket/i,
  /note/i,
  /^(body|text|content|message|messages|description|bio|answers?|comment|words|brief)$/i,
  /(Body|Text|Content|Message|Messages|Description|Answers?|Comment)$/,
  /feedback|rationale|evidence|explanation|triggerFacts|contextSnapshot/i,
  // A reason someone wrote (pauseReason, failureReason). A bare `reason` is
  // usually a code (`openai_error`) and stays.
  /.Reason$/,
  /(from|to|response)Value$/i,
  /snapshot$|summary$/i,
  /valueJson/i,
  // A wearable's readings: heartRateData, sleepDurationsData, metData.
  /.Data$/,
]

// A presence flag: `isInsuranceClient`, `hasMemberId`. MySQL returns booleans
// as 0 or 1, so those count as flags too under a key shaped like one.
const PRESENCE_PREFIX = /^(has|is|was|are|were|should|can|did|will)[A-Z]/

type Mode = 'none' | 'detail' | 'personal'

const matches = (patterns: RegExp[], key: string) =>
  patterns.some(pattern => pattern.test(key))

const camelKey = (key: string) =>
  key
    .replace(/^[-_\s]+/, '')
    .replace(/[-_\s]+(.)?/g, (_, next: string | undefined) =>
      next ? next.toUpperCase() : ''
    )

const modeForKey = (key: string, parent: Mode): Mode => {
  if (matches(SENSITIVE_ID_PATTERNS, key)) return 'personal'
  if (
    matches(IDENTIFIER_PATTERNS, key) ||
    SAFE_NAME_KEYS.has(key) ||
    SCRUBBED_TEXT_KEYS.has(key)
  ) {
    return 'none'
  }
  if (matches(PERSONAL_PATTERNS, key)) return 'personal'
  if (parent === 'personal') return 'personal'
  if (matches(DETAIL_PATTERNS, key)) return 'detail'
  return 'none'
}

const isKeptFlag = (key: string, value: unknown) =>
  (typeof value === 'boolean' ||
    ((value === 0 || value === 1) && PRESENCE_PREFIX.test(key))) &&
  !matches(SENSITIVE_ID_PATTERNS, key) &&
  !matches(HEALTH_PATTERNS, key)

/** Exported for the tests: whether a key's scalar value is replaced. */
export const isPersonalKey = (key: string) =>
  modeForKey(camelKey(key), 'none') !== 'none'

// Emails and phone numbers typed into a message or a URL. Separated or E.164
// numbers only: a bare 10-digit run is as likely an id or a timestamp.
const TEXT_PATTERNS: [RegExp, string][] = [
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,}/g, FILTERED],
  [
    /(^|[^\w.])((?:\+1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]\d{3}[\s.-]\d{4})(?!\w|\.\d)/g,
    `$1${FILTERED}`,
  ],
  [/(^|[^\w.])\+\d{10,15}(?!\w|\.\d)/g, `$1${FILTERED}`],
]

/** Text with emails and phone numbers replaced. */
export const redactPersonalText = (text: string): string => {
  if (typeof text !== 'string') return text
  return TEXT_PATTERNS.reduce(
    (redacted, [pattern, replacement]) =>
      redacted.replace(pattern, replacement),
    text
  )
}

const isEmpty = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0)

interface WalkState {
  ancestors: Set<object>
  nodes: number
  redactString: (text: string) => string
}

// An error is its class, its code and status, and its scrubbed message. Its
// other fields (an Apollo error's network response, a fetch's request) are not
// walked.
const summarizeError = (error: Error, state: WalkState) => {
  const { code, status, extensions, networkError } = error as Error & {
    code?: unknown
    status?: unknown
    extensions?: { code?: unknown }
    networkError?: { statusCode?: unknown }
  }
  const errorCode = extensions?.code ?? code
  const errorStatus = networkError?.statusCode ?? status
  return {
    name: error.name,
    message: state.redactString(error.message),
    ...(typeof errorCode === 'string' && { code: errorCode }),
    ...(typeof errorStatus === 'number' && { status: errorStatus }),
  }
}

const redactValue = (
  value: unknown,
  mode: Mode,
  depth: number,
  state: WalkState
): unknown => {
  if (isEmpty(value)) return value
  if (value instanceof Error) return summarizeError(value, state)

  if (typeof value !== 'object') {
    if (mode !== 'none' && typeof value !== 'function') return FILTERED
    return typeof value === 'string' ? state.redactString(value) : value
  }

  if (state.ancestors.has(value)) return CIRCULAR
  if (depth >= MAX_DEPTH || state.nodes >= MAX_NODES) return TRUNCATED
  state.nodes += 1

  state.ancestors.add(value)
  try {
    const toJSON = (value as { toJSON?: () => unknown }).toJSON
    if (typeof toJSON === 'function') {
      const json = toJSON.call(value)
      // Deeper by one, so a toJSON that keeps returning new objects ends.
      if (json !== value) return redactValue(json, mode, depth + 1, state)
    }
    if (Array.isArray(value)) {
      return value.map(item => redactValue(item, mode, depth + 1, state))
    }
    const parent = mode === 'personal' ? 'personal' : 'none'
    return Object.fromEntries(
      Object.entries(value).map(([rawKey, nested]) => [
        rawKey,
        redactEntry(rawKey, nested, parent, depth + 1, state),
      ])
    )
  } finally {
    state.ancestors.delete(value)
  }
}

const redactEntry = (
  rawKey: string,
  value: unknown,
  parent: Mode,
  depth: number,
  state: WalkState
): unknown => {
  if (isEmpty(value)) return value
  const key = camelKey(rawKey)
  if (isKeptFlag(key, value)) return value
  try {
    return redactValue(value, modeForKey(key, parent), depth, state)
  } catch {
    return UNREDACTABLE
  }
}

/**
 * A copy of report data (a metadata section, breadcrumb metadata) with
 * personal values replaced at any depth: errors become `{ name, message }`
 * (plus a code and status when they have them), a value whose walk throws
 * becomes `[Unredactable]`,
 * models are walked through their JSON, strings that survive go through
 * `redactString` (emails and phone numbers by default), and ids, flags and
 * null or empty values stay. Never mutates its input.
 */
export const redactReportData = (
  value: unknown,
  redactString: (text: string) => string = redactPersonalText
): unknown => {
  try {
    return redactValue(value, 'none', 0, {
      ancestors: new Set(),
      nodes: 0,
      redactString,
    })
  } catch {
    return UNREDACTABLE
  }
}
